import { Card, CardContent, CardHeader, CardTitle } from '@helios/ui';
import { StatusIndicator } from '@/components/data-display/StatusIndicator';
import { useSystemStatus } from '@/hooks/useSystemStatus';
import type { SocketStatus } from '@/hooks/useRealtimeDashboard';
import type { ServiceHealth } from '@/types';

function toHealth(value: 'up' | 'down' | 'skipped' | 'unknown'): ServiceHealth {
  if (value === 'up') return 'up';
  if (value === 'down') return 'down';
  if (value === 'skipped') return 'degraded';
  return 'unknown';
}

function realtimeHealth(status: SocketStatus): ServiceHealth {
  if (status === 'connected') return 'up';
  if (status === 'connecting') return 'degraded';
  if (status === 'error') return 'down';
  return 'down';
}

function realtimeDetail(status: SocketStatus): string {
  if (status === 'connected') return 'WebSocket · live';
  if (status === 'connecting') return 'WebSocket · connecting';
  if (status === 'error') return 'WebSocket · connection error';
  return 'WebSocket · disconnected';
}

export function SystemStatusArea({
  realtimeStatus = 'disconnected',
}: {
  realtimeStatus?: SocketStatus;
}) {
  const { status, isLoading } = useSystemStatus();

  const apiHealth: ServiceHealth = isLoading ? 'unknown' : status.reachable ? 'up' : 'down';
  const dbHealth: ServiceHealth = status.reachable ? toHealth(status.database) : 'unknown';
  const storageConfigured = status.objectStorage !== 'skipped';
  const storageHealth: ServiceHealth = status.reachable
    ? toHealth(status.objectStorage)
    : 'unknown';
  const storageDetail = !storageConfigured
    ? 'Object storage not configured'
    : storageHealth === 'up'
      ? 'MinIO / S3 · /api/health'
      : 'MinIO / S3 · unhealthy or unreachable';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">System Status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <StatusIndicator label="API Gateway" status={apiHealth} detail="GET /api/health" />
        <StatusIndicator label="Database" status={dbHealth} detail="Postgres · Prisma" />
        <StatusIndicator
          label="Realtime"
          status={realtimeHealth(realtimeStatus)}
          detail={realtimeDetail(realtimeStatus)}
        />
        <StatusIndicator label="Object Storage" status={storageHealth} detail={storageDetail} />
      </CardContent>
    </Card>
  );
}
